import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Mic, Volume2, Pause, RotateCcw, Download, Copy, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Voice {
  id: string;
  name: string;
  description: string;
  gender: 'male' | 'female' | 'neutral';
  accent: string;
  style: string;
}

const voices: Voice[] = [
  { id: 'emma', name: 'Emma', description: 'Natural & Friendly', gender: 'female', accent: 'US', style: 'Warm, conversational tone perfect for everyday use' },
  { id: 'james', name: 'James', description: 'Professional & Clear', gender: 'male', accent: 'UK', style: 'Articulate and authoritative for business' },
  { id: 'sophia', name: 'Sophia', description: 'Energetic & Youthful', gender: 'female', accent: 'US', style: 'Upbeat and engaging for creative content' },
  { id: 'oliver', name: 'Oliver', description: 'Deep & Trustworthy', gender: 'male', accent: 'US', style: 'Rich, resonant voice for narration' },
  { id: 'nova', name: 'Nova AI', description: 'Futuristic & Smooth', gender: 'neutral', accent: 'Global', style: 'Advanced AI voice with perfect clarity' },
];

const sampleTexts = [
  "Welcome to the future of text input. Just speak naturally and watch your words appear instantly.",
  "Transform your ideas into text at the speed of thought. No more typing fatigue.",
  "Perfect for writers, students, professionals, and anyone who values their time.",
  "Experience the freedom of hands-free text creation with industry-leading accuracy.",
];

export function TTSPlayground() {
  const [selectedVoice, setSelectedVoice] = useState(voices[0].id);
  const [text, setText] = useState(sampleTexts[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [charCount, setCharCount] = useState(text.length);
  const [wordCount, setWordCount] = useState(text.split(/\s+/).filter(w => w.length > 0).length);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setCharCount(text.length);
    setWordCount(text.split(/\s+/).filter(w => w.length > 0).length);
  }, [text]);

  const handlePlay = () => {
    setIsPlaying(!isPlaying);
    // Simulate playing
    if (!isPlaying) {
      setTimeout(() => setIsPlaying(false), 3000);
    }
  };

  const handleRecord = () => {
    setIsRecording(!isRecording);
    if (!isRecording) {
      // Simulate recording
      setText('');
      let sampleIndex = 0;
      const interval = setInterval(() => {
        if (sampleIndex < sampleTexts[1].length) {
          setText(prev => prev + sampleTexts[1][sampleIndex]);
          sampleIndex++;
        } else {
          clearInterval(interval);
          setIsRecording(false);
        }
      }, 50);
    }
  };

  const handleReset = () => {
    setText(sampleTexts[0]);
    setIsPlaying(false);
    setIsRecording(false);
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    // Show toast or feedback
  };

  const handleRandomText = () => {
    const randomIndex = Math.floor(Math.random() * sampleTexts.length);
    setText(sampleTexts[randomIndex]);
  };

  const currentVoice = voices.find(v => v.id === selectedVoice)!;

  return (
    <section id="playground" className="py-24 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60rem] h-[30rem] bg-primary/10 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">Interactive Demo</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold">
              Try It Right Now
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Experience the power of our text-to-speech technology. Test different voices, 
              record your own text, or type anything you want to hear.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {/* Left Side - Controls */}
            <div className="space-y-6">
              {/* Voice Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Choose Your Voice</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {voices.map((voice) => (
                        <SelectItem key={voice.id} value={voice.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{voice.name}</span>
                            <span className="text-sm text-muted-foreground">- {voice.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {/* Voice Details */}
                  <div className="p-4 bg-muted/50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Voice Profile</span>
                      <span className={cn(
                        "text-xs px-2 py-1 rounded-full",
                        currentVoice.gender === 'female' ? 'bg-pink-500/10 text-pink-500' :
                        currentVoice.gender === 'male' ? 'bg-blue-500/10 text-blue-500' :
                        'bg-purple-500/10 text-purple-500'
                      )}>
                        {currentVoice.gender}
                      </span>
                    </div>
                    <p className="text-sm">{currentVoice.style}</p>
                    <div className="flex gap-2 mt-2">
                      <span className="text-xs bg-background px-2 py-1 rounded">
                        {currentVoice.accent} Accent
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Action Buttons */}
              <Card>
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={handlePlay}
                      disabled={!text || isRecording}
                      className="group"
                      size="lg"
                    >
                      {isPlaying ? (
                        <>
                          <Pause className="mr-2 h-5 w-5" />
                          Pause
                        </>
                      ) : (
                        <>
                          <Volume2 className="mr-2 h-5 w-5 transition-transform group-hover:scale-110" />
                          Play Text
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleRecord}
                      variant={isRecording ? "destructive" : "secondary"}
                      size="lg"
                      className="group"
                    >
                      <Mic className={cn(
                        "mr-2 h-5 w-5",
                        isRecording && "animate-pulse"
                      )} />
                      {isRecording ? 'Recording...' : 'Record'}
                    </Button>
                    <Button
                      onClick={handleCopy}
                      variant="outline"
                      size="lg"
                      disabled={!text}
                    >
                      <Copy className="mr-2 h-4 w-4" />
                      Copy
                    </Button>
                    <Button
                      onClick={handleReset}
                      variant="outline"
                      size="lg"
                    >
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Reset
                    </Button>
                  </div>

                  <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                    <span>{wordCount} words • {charCount} characters</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleRandomText}
                    >
                      <Sparkles className="mr-2 h-3 w-3" />
                      Random Text
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Download CTA */}
              <Card className="border-primary/20 bg-primary/5">
                <CardContent className="pt-6">
                  <div className="text-center space-y-3">
                    <p className="font-semibold">Love what you hear?</p>
                    <p className="text-sm text-muted-foreground">
                      Download the full app for unlimited text-to-speech with offline support
                    </p>
                    <Button className="w-full" size="lg">
                      <Download className="mr-2 h-4 w-4" />
                      Download for Windows
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Side - Text Area */}
            <div className="space-y-6">
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span>Your Text</span>
                    {isPlaying && (
                      <div className="flex items-center gap-2 text-sm text-primary">
                        <div className="h-2 w-2 bg-primary rounded-full animate-pulse" />
                        Speaking...
                      </div>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative">
                    <textarea
                      ref={textareaRef}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder="Type or speak your text here..."
                      className={cn(
                        "w-full min-h-[400px] p-4 rounded-lg border bg-background resize-none transition-all",
                        "focus:outline-none focus:ring-2 focus:ring-primary/50",
                        isRecording && "border-primary animate-pulse"
                      )}
                      disabled={isRecording}
                    />
                    
                    {/* Visual Feedback */}
                    {isPlaying && (
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="h-1 bg-muted rounded-full overflow-hidden">
                          <div className="h-full bg-primary rounded-full animate-pulse" style={{ width: '60%' }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Tips */}
                  <div className="mt-4 p-3 bg-muted/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">
                      <strong>Pro tip:</strong> In the desktop app, you can use voice commands like 
                      "new paragraph", "comma", or "period" for punctuation while speaking.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}